import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerNotificationTable } from '../../utils/constants';
import { INotification } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import withComponentAuth from '../../hoc/withComponentAuth';
import { GETNotifications } from '../../services/notification';
import { PromotionList } from '../../components/ui/list/Promotions';
import PushPromotionalNotification from '../../components/ui/add/promotion';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const NotificationsManagement = () => {
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);

  // Fetch users whenever page, rowsPerPage, or searchQuery changes
  useEffect(() => {
    fetchUsers(page, rowsPerPage, searchQuery, sort);
  }, [page, rowsPerPage, searchQuery, sort]);

  // Fetch users from API
  const fetchUsers = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GETNotifications({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });
      setNotifications(response.data); // Set the user data from the API
      setTotalCount(response.count); // Set the total number of users
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refeshList = () => {
    fetchUsers(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search); // Update the search query state
    setPage(1); // Reset to the first page for new search
  }, 500); // 500ms delay before search executes

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  const handleSortRefresh = () => {
    setSort(sort == 'ASC' ? 'DESC' : 'ASC');
    refeshList();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="Promotions"
        createFilter={['SEARCH', 'ADD']}
        addElement={<PushPromotionalNotification />}
        filterHeader={'Filter Users'}
        addHeader={'Promotional Notification'}
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {notifications.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Notifications Found
            </Typography>
          ) : (
            <PromotionList
              notifications={notifications}
              onUpdate={refeshList}
              isLoading={isLoading}
              headerCells={headerNotificationTable}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalCount} // Use totalUsers from API response
            page={page - 1} // Convert 1-based to 0-based page index for the pagination component
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(NotificationsManagement);
